import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,

} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { useRef } from "react";
import { useHistoricalDataContext } from "../../HistoricalDataContext";
import "chartjs-adapter-moment";
import classes from "./HistoricalEvolutionGraph.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  zoomPlugin
);

interface HistoricalEvolutionGraphProps {
  title: string;
  dataSet: DataSet[];
  borderColor: string;
  backgroundColor: string;
  specificDate: Date | undefined;
}

interface DataSet {
  value: number;
  recvTime: Date;
  timestamp?: Date;
}

const HistoricalEvolutionGraph = ({
  title,
  dataSet,
  specificDate,
  borderColor,
  backgroundColor,
}: HistoricalEvolutionGraphProps) => {
  const {
    setAlertStartDate,
    setAlertEndDate,
    setGeneralStartDate,
    setGeneralEndDate,
    generalStartDate,
    generalEndDate,
  } = useHistoricalDataContext();
  const chartRef = useRef<any>(null);

  // Filter dataSet based on generalStartDate and generalEndDate
  const filteredDataSet = dataSet.filter((data) => {
    const dataDate = new Date(data.timestamp || data.recvTime);
    return dataDate >= generalStartDate && dataDate <= generalEndDate;
  });

  let specificIndex: number | undefined;
  if (specificDate !== undefined) {
    const specificDateObj = new Date(specificDate);
    specificDateObj.setMilliseconds(0);  // Establecer milisegundos a 0

    specificIndex = filteredDataSet.findIndex((data) => {
      // Usar timestamp si está disponible, sino recvTime
      const dataDate = new Date(data.timestamp || data.recvTime);
      dataDate.setMilliseconds(0);
      return dataDate.getTime() === specificDateObj.getTime();
    });
  }

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: "",
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            let value: number = tooltipItem.parsed.y;
            let datasetLabel = tooltipItem.dataset.label || "";

            if (datasetLabel.toLowerCase().includes("conductividad")) {
              return `${datasetLabel}: ${value} µS/cm`;
            } else if (datasetLabel.toLowerCase().includes("temperatura")) {
              return `${datasetLabel}: ${value} °C`;
            }

            return `${datasetLabel}: ${value}`;
          },
        },
      },
      zoom: {
        pan: { enabled: false },
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgba(0, 123, 255, 0.2)",
            borderColor: "rgba(0, 123, 255, 0.8)",
            borderWidth: 1,
          },
          mode: "x" as const,
          onZoomComplete: ({ chart }: any) => {
            const xScale = chart.scales.x;
            const start = new Date(xScale.min);
            const end = new Date(xScale.max);
            setAlertStartDate(start);
            setAlertEndDate(end);
            setGeneralStartDate(start);
            setGeneralEndDate(end);
          },
        },
      },
    },
    scales: {
      x: {
        type: "time" as const,
        gridLines: {
          lineWidth: 2,
        },
        time: {
          unit: "day" as const,
          unitStepSize: 1000,
          displayFormats: {
            millisecond: "MMM DD",
            second: "MMM DD",
            minute: "MMM DD",
            hour: "MMM DD",
            day: "MMM DD",
            week: "MMM DD",
            month: "MMM DD",
            quarter: "MMM DD",
            year: "MMM DD",
          },
        },
      },
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
      },
    },
  };

  const transformData = (
    dataSet: DataSet[],
    label: string,
    borderColor: string,
    backgroundColor: string
  ) => {
    return {
      label,
      data: dataSet.map((data) => ({
        x: new Date(data.timestamp || data.recvTime), // Usar timestamp si está disponible
        y: data.value,
      })),
      borderColor: borderColor,
      backgroundColor: backgroundColor,
      yAxisID: "y",
      pointRadius: dataSet.map((_, index) => {
        const conditionMet = typeof specificIndex === "number" && index === specificIndex;
        return conditionMet ? 5 : 0;
      }),
      pointBackgroundColor: dataSet.map(
        (_, index) => (index === specificIndex ? "#007FFF" : backgroundColor)
      ),
    };
  };

  const chartData = {
    label: filteredDataSet.map((data) => new Date(data.timestamp || data.recvTime)), // Usar timestamp si está disponible
    datasets: [transformData(filteredDataSet, title, borderColor, backgroundColor)],
  };

  return (
    <section>
      <div className={classes.card}>
        <div className={classes.card_content}>
          <div className={classes.graphContainer}>
            <Line ref={chartRef} options={options} data={chartData} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HistoricalEvolutionGraph